import { useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import type { NewProgramToken, ProgramTokens } from '../api/types'
import { errorMessage } from '../api/client'
import { useMounted } from '../components/layout'
import { useSessionsEnv } from '../sessions/context'
import { useBoardState, useStore } from '../store/context'

export function ProgramTokensSettings() {
  const { me } = useBoardState()
  const store = useStore()
  if (store.byToken || !me?.member) return null
  return <Programs key={`${me.workspace.id}/${me.member.id}`} member={me.member.id} />
}

function Programs({ member }: { member: string }) {
  const { api } = useSessionsEnv()
  const { offline } = useBoardState()
  const mounted = useMounted()
  const [listing, setListing] = useState<ProgramTokens | null>(null)
  const [name, setName] = useState('')
  const [scopes, setScopes] = useState(['read'])
  const [created, setCreated] = useState<NewProgramToken | null>(null)
  const [busy, setBusy] = useState(false)
  const [reading, setReading] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [attempt, refresh] = useState(0)
  useEffect(() => {
    let active = true
    if (offline) return
    setReading(true); setProblem(null)
    api.programTokens(member).then(value => { if (active) setListing(value) }, error => {
      if (active) setProblem(errorMessage(error))
    }).finally(() => { if (active) setReading(false) })
    return () => { active = false }
  }, [api, member, offline, attempt])

  async function change(id?: string) {
    if (busy || offline) return
    setBusy(true); setProblem(null); setCreated(null)
    try {
      if (id) await api.revokeProgramToken(id, member)
      else {
        const value = await api.createProgramToken({ name, scopes }, member)
        if (!mounted.current) return
        setCreated(value); setName('')
      }
      if (mounted.current) refresh(n => n + 1)
    } catch (error) {
      if (mounted.current) {
        setProblem(`${errorMessage(error)} Refresh to check whether it changed. A token’s secret cannot be shown again.`)
      }
    } finally { if (mounted.current) setBusy(false) }
  }
  return <section className="settings-section">
    <p>Named tokens for programs and agents outside Poulpe. Each acts as you, with only the access you choose, across this workspace. Tokens expire after 90 days.</p>
    <p><Link to="/settings/workspace/tools">Teach your agents Poulpe</Link></p>
    <button className="button" disabled={busy || reading || offline} onClick={() => refresh(n => n + 1)}>Refresh</button>
    {reading && <p role="status">Reading programs…</p>}
    {created && <section className="settings-group" aria-label="New token">
      <p>Copy this token now. It will not be shown again.</p>
      <label className="field"><span>Token</span><textarea readOnly value={created.secret} autoComplete="off" spellCheck={false} /></label>
      <button className="button" onClick={() => setCreated(null)}>Done copying</button>
    </section>}
    {listing && <>
      {listing.programs.length === 0 && <p>No programs yet.</p>}
      {listing.programs.map(row => <div key={row.id} className="settings-group">
        <p><strong>{row.name}</strong> · {row.scopes.join(', ')}</p>
        <p className="muted">Expires {new Date(row.expires_at).toLocaleDateString()}</p>
        {row.revoked_at ? <p>Revoked</p> : <button className="button" disabled={busy || reading || offline} onClick={() => void change(row.id)}>Revoke {row.name}</button>}
      </div>)}
      <form className="setup-form" onSubmit={(event: FormEvent) => { event.preventDefault(); void change() }}>
        <fieldset className="settings-group" disabled={busy || reading || offline}>
          <label className="field"><span>Name</span><input required maxLength={60} pattern="[a-z][a-z0-9]*(-[a-z0-9]+)*" value={name} onChange={e => setName(e.target.value)} /></label>
          <fieldset className="settings-group"><legend>Access</legend>
            {listing.scopes.map(scope => <label key={scope} style={{ display: 'block' }}>
              <input type="checkbox" checked={scopes.includes(scope)} onChange={e => setScopes(held => e.target.checked ? [...held, scope] : held.filter(s => s !== scope))} /> {scope}
            </label>)}
          </fieldset>
          <button className="button" disabled={!name || scopes.length === 0}>Create token</button>
        </fieldset>
      </form>
      <p className="muted">The list shows your live tokens and recent history. Revoke an unused token before creating another with its name.</p>
    </>}
    {offline && <p role="status">Connect to the server to manage program tokens.</p>}
    {problem && <p role="alert" className="text-danger">{problem}</p>}
  </section>
}

/** Configuration contains an environment variable, never the person's secret. */
export function mcpConfiguration(url: string) {
  return {
    claude: JSON.stringify({ mcpServers: { poulpe: { type: 'http', url, headers: { Authorization: 'Bearer ${POULPE_TOKEN}' } } } }, null, 2),
    codex: `[mcp_servers.poulpe]\nurl = ${JSON.stringify(url)}\nbearer_token_env_var = "POULPE_TOKEN"`,
  }
}

export function TeachSettings() {
  const { me, offline } = useBoardState()
  const store = useStore()
  if (store.byToken || !me?.member) return null
  return <Teach key={`${me.workspace.id}/${me.member.id}`} member={me.member.id} offline={offline} />
}
function Teach({ member, offline }: { member: string; offline: boolean }) {
  const { api } = useSessionsEnv()
  const [url, setUrl] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [attempt, refresh] = useState(0)
  useEffect(() => {
    let active = true
    if (offline) return
    setProblem(null)
    api.programTokens(member).then(value => { if (active) setUrl(value.mcp_url) }, error => { if (active) setProblem(errorMessage(error)) })
    return () => { active = false }
  }, [api, member, offline, attempt])
  const config = url ? mcpConfiguration(url) : null
  return <section className="settings-section">
    <p>A session can read and file issues, record its work and ask you a question.</p>
    <p>Sessions started in Poulpe already have its tools. Nothing is written for them.</p>
    <p><Link to="/settings/workspace/programs">Create a program token</Link> with the access your agent needs. Set POULPE_TOKEN in the environment where you start Claude Code or Codex. Keep the token out of Git.</p>
    {config && <>
      <h2 className="settings-title">Claude Code</h2>
      <p>Add this server to .mcp.json in your repository.</p><pre>{config.claude}</pre>
      <h2 className="settings-title">Codex</h2>
      <p>Add this server to .codex/config.toml. Codex reads it once you trust this folder in Codex.</p><pre>{config.codex}</pre>
      <h2 className="settings-title">Guidance</h2>
      <p>Add a section in AGENTS.md so your agents know that work is tracked in Poulpe. If Claude Code uses CLAUDE.md, add the line @AGENTS.md there.</p>
      <pre>{'Use Poulpe’s MCP tools to read and file issues, record your work and ask a person.\nChoose the project before filing work. Use only the access your token grants.\nHosted sessions, delegation and proposals are managed inside Poulpe.'}</pre>
    </>}
    <h2 className="settings-title">Repository hooks</h2>
    <p>Pinning repository hooks, accepting them on a Mac and managing protected paths have no settings counterpart yet. Agents use their own configuration on the runner.</p>
    <h2 className="settings-title">Terminal hooks</h2>
    <p>Adding and removing Poulpe’s terminal bridge hooks for Claude Code and Codex has no counterpart yet. Manage your agent’s hooks in its own app.</p>
    {offline && <p role="status">Connect to the server to read its MCP address.</p>}
    {problem && <><p role="alert" className="text-danger">{problem}</p><button className="button" onClick={() => refresh(n => n + 1)}>Refresh</button></>}
  </section>
}
